import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type ChangeEvent,
} from "react";
import { useClickOutside } from "hooks";
import { constrainValue } from "utils";
import { InputComponent } from "./InputComponent";

type NumberInputWithClickOutsideProps = {
  initialValue: number;
  onSave: (n: number) => void;
  allowOutsidePropagation: boolean;
  min: number;
  max: number;
  disabled: boolean;
  onClickOutside: () => void;
  classNames: string;
  testId: string;
};

export const NumberInputWithClickOutside = ({
  initialValue,
  onSave,
  allowOutsidePropagation = true,
  min = 0,
  max,
  disabled,
  onClickOutside = () => {},
  classNames,
  testId,
}: NumberInputWithClickOutsideProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef(null);
  const initialValueRef = useRef<number>(initialValue); // Store initial value in a ref

  const [inputValue, setInputValue] = useState<number>(initialValue || 0);

  const handlePlusClick = useCallback(() => {
    if (disabled) {
      return;
    }
    const valueToSave = constrainValue(+inputValue + 1, min, max);
    setInputValue(valueToSave);
  }, [disabled, inputValue, min, max]);

  const handleMinusClick = useCallback(() => {
    if (disabled) {
      return;
    }
    const valueToSave = constrainValue(+inputValue - 1, min, max);
    setInputValue(valueToSave);
  }, [disabled, inputValue, min, max]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(constrainValue(parseFloat(e.target.value), min, max));
    },
    [min, max],
  );

  const handleSave = useCallback(() => {
    if (inputValue !== initialValueRef.current) {
      const valueToSave = constrainValue(+inputValue, min, max);
      setInputValue(valueToSave);
      onSave(valueToSave);
    }
    onClickOutside();
    setIsFocused(false);
  }, [inputValue, max, min, onSave, onClickOutside]);

  // Update initialValueRef if initialValue changes
  useEffect(() => {
    initialValueRef.current = initialValue;
  }, [initialValue]);

  const handleEnterPressed = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      }
    },
    [handleSave],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEnterPressed);
    return () => document.removeEventListener("keydown", handleEnterPressed);
  }, [handleEnterPressed]);

  useClickOutside(inputRef, handleSave, isFocused, allowOutsidePropagation);

  return (
    <InputComponent
      ref={inputRef}
      classNames={classNames}
      value={inputValue}
      min={min}
      max={max}
      disabled={disabled}
      testId={testId}
      handleMinusClick={handleMinusClick}
      handlePlusClick={handlePlusClick}
      handleChange={handleChange}
      onBlur={(e: FocusEvent<HTMLInputElement>) =>
        !e.target.value && setInputValue(min)
      }
      onMouseEnter={() => setIsFocused(true)}
    />
  );
};
